import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import sellerApi from "@/lib/sellerApi";
import { formatApiError, fileUrl } from "@/lib/api";
import { rupee } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Package, Plus, Pencil, Trash2, Upload, Loader2, X } from "lucide-react";

const empty = { name: "", description: "", category_id: "", subcategory_id: "", brand: "", mrp: "", price: "", weight: "", sku: "", stock: "", images: [], is_active: true };

export default function SellerProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: c }] = await Promise.all([
        sellerApi.get("/seller/products"),
        sellerApi.get("/seller/categories"),
      ]);
      setProducts(p);
      setCategories(c);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (form.category_id) {
      sellerApi.get("/seller/subcategories", { params: { category_id: form.category_id } })
        .then(({ data }) => setSubcats(data)).catch(() => setSubcats([]));
    } else {
      setSubcats([]);
    }
  }, [form.category_id]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const openAdd = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, description: p.description || "", category_id: p.category_id, subcategory_id: p.subcategory_id || "", brand: p.brand || "",
      mrp: p.mrp, price: p.selling_price ?? p.price, weight: p.weight || "", sku: p.sku || "",
      stock: p.stock, images: p.images || [], is_active: p.is_active,
    });
    setEditId(p.id);
    setOpen(true);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await sellerApi.post("/seller/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.file_id] }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Enter a product name");
    if (!form.category_id) return toast.error("Select a category");
    if (!(Number(form.mrp) > 0) || !(Number(form.price) > 0)) return toast.error("Enter valid MRP and selling price");
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description, category_id: form.category_id,
        subcategory_id: form.subcategory_id || null, brand: form.brand,
        mrp: Number(form.mrp), price: Number(form.price), weight: form.weight, sku: form.sku,
        stock: Number(form.stock) || 0, images: form.images, is_active: form.is_active,
      };
      if (editId) await sellerApi.put(`/seller/products/${editId}`, payload);
      else await sellerApi.post("/seller/products", payload);
      toast.success(editId ? "Product updated" : "Product added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, is_active: !x.is_active } : x)));
    try { await sellerApi.patch(`/seller/products/${p.id}/toggle`); }
    catch (err) { setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, is_active: p.is_active } : x))); toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await sellerApi.delete(`/seller/products/${p.id}`); toast.success("Product deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="seller-products">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Products</h1>
          <p className="text-ink-soft">Add and manage the products your shop sells on NICEE.</p>
        </div>
        <Button onClick={openAdd} className="rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="seller-add-product"><Plus className="mr-1 h-4 w-4" /> Add Product</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : products.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/60 p-12 text-center">
          <Package className="mx-auto h-10 w-10 text-ink-muted" />
          <p className="mt-3 font-heading text-lg text-ink">No products yet</p>
          <p className="text-sm text-ink-muted">Click “Add Product” to list your first item.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">MRP / Selling</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-border/50 last:border-0" data-testid={`seller-product-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.image_id ? <img src={fileUrl(p.image_id)} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-cream-alt text-ink-muted"><Package className="h-5 w-5" /></span>}
                      <div>
                        <p className="font-semibold text-ink">{p.name}</p>
                        <p className="text-xs text-ink-muted">{p.brand || "—"} {p.weight ? `· ${p.weight}` : ""}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{catName(p.category_id)}</td>
                  <td className="px-4 py-3 text-ink-soft"><span className="line-through opacity-60">{rupee(p.mrp)}</span> · <span className="font-semibold text-ink">{rupee(p.selling_price ?? p.price)}</span></td>
                  <td className="px-4 py-3"><span className={p.stock <= 0 ? "font-semibold text-destructive" : "text-ink-soft"}>{p.stock}</span></td>
                  <td className="px-4 py-3"><Switch checked={!!p.is_active} onCheckedChange={() => toggle(p)} data-testid={`seller-toggle-${p.id}`} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(p)} data-testid={`seller-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => remove(p)} data-testid={`seller-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Product Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" data-testid="product-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Brand</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1 rounded-xl" data-testid="product-brand-input" /></div>
              <div>
                <Label>Category</Label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value, subcategory_id: "" })} className="mt-1 h-10 w-full rounded-xl border border-border bg-white px-3 text-sm outline-none focus:border-brand" data-testid="product-category-select">
                  <option value="">Select…</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            {subcats.length > 0 && (
              <div>
                <Label>Sub-category</Label>
                <select value={form.subcategory_id} onChange={(e) => setForm({ ...form, subcategory_id: e.target.value })} className="mt-1 h-10 w-full rounded-xl border border-border bg-white px-3 text-sm outline-none focus:border-brand" data-testid="product-subcategory-select">
                  <option value="">None</option>
                  {subcats.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div><Label>MRP (₹)</Label><Input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="mt-1 rounded-xl" data-testid="product-mrp-input" /></div>
              <div><Label>Selling (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 rounded-xl" data-testid="product-price-input" /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1 rounded-xl" data-testid="product-stock-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Weight / Qty</Label><Input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="e.g. 500g" className="mt-1 rounded-xl" data-testid="product-weight-input" /></div>
              <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1 rounded-xl" data-testid="product-sku-input" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-xl" rows={2} /></div>
            <div>
              <Label>Product Images</Label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {form.images.map((id) => (
                  <div key={id} className="relative"><img src={fileUrl(id)} alt="" className="h-16 w-16 rounded-xl object-cover" /><button type="button" onClick={() => setForm({ ...form, images: form.images.filter((x) => x !== id) })} className="absolute -right-1 -top-1 rounded-full bg-ink p-0.5 text-white"><X className="h-3 w-3" /></button></div>
                ))}
                <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="product-image-input" /><span className="flex h-16 w-16 items-center justify-center rounded-xl border border-dashed border-ink/25 text-ink-muted hover:border-brand">{uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}</span></label>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-cream-alt/50 px-4 py-2.5">
              <Label>Active (visible to customers)</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="product-active-switch" />
            </div>
            <Button onClick={save} disabled={saving} className="w-full rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="save-product-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Update Product" : "Add Product")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
